import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Car, MapPin, Clock, CheckCircle2, XCircle, Phone, Mail, User, Send, FileText, Flag } from "lucide-react";
import localApi from "@/lib/localApi";
import { money, fmtDate } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StarInput } from "@/components/StarInput";
import { toast } from "sonner";

const STATUS = {
  pending: { icon: Clock, cls: "text-amber-400 bg-amber-400/10 border-amber-400/20", label: "Awaiting client" },
  accepted: { icon: CheckCircle2, cls: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20", label: "Accepted" },
  declined: { icon: XCircle, cls: "text-slate-400 bg-slate-400/10 border-slate-400/20", label: "Not selected" },
};

function RateClientForm({ reqId, onDone }) {
  const [s, setS] = useState({ payment: 0, communication: 0, respectfulness: 0 });
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!s.payment || !s.communication || !s.respectfulness) { toast.error("Please rate all categories"); return; }
    setSaving(true);
    try {
      await localApi.post(`/marketplace/requests/${reqId}/rate-client`, { ...s, comment });
      toast.success("Thanks for rating this client!");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit rating"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-slate-800" data-testid={`rate-client-form-${reqId}`}>
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Rate this client</p>
      <div className="space-y-1.5">
        <StarInput label="Payment reliability" value={s.payment} onChange={(v) => setS((p) => ({ ...p, payment: v }))} testid={`rc-payment-${reqId}`} />
        <StarInput label="Communication" value={s.communication} onChange={(v) => setS((p) => ({ ...p, communication: v }))} testid={`rc-communication-${reqId}`} />
        <StarInput label="Respectfulness" value={s.respectfulness} onChange={(v) => setS((p) => ({ ...p, respectfulness: v }))} testid={`rc-respectfulness-${reqId}`} />
      </div>
      <Textarea data-testid={`rc-comment-${reqId}`} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional note (private to Pitcrew)" className="mt-3 bg-slate-950 border-slate-700 text-sm" />
      <Button size="sm" data-testid={`rc-submit-${reqId}`} onClick={submit} disabled={saving} className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
        {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Submit rating
      </Button>
    </div>
  );
}

export default function MyBids() {
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState("");

  const load = () => localApi.get("/marketplace/my-bids").then(({ data }) => setBids(data.bids || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const completeJob = async (reqId) => {
    if (!window.confirm("Mark this job as completed? Both you and the client will then be able to rate each other.")) return;
    setCompleting(reqId);
    try {
      await localApi.post(`/marketplace/requests/${reqId}/complete`);
      toast.success("Job marked completed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete job"); }
    finally { setCompleting(""); }
  };

  const createEstimate = ({ bid, request, client }) => {
    navigate("/invoices/new", {
      state: {
        prefill: {
          doc_type: "estimate", marketplaceReqId: request.id,
          customer_name: client?.name || "", customer_phone: client?.phone || "", customer_email: client?.email || "",
          vehicle_year: request.vehicle_year || "", vehicle_make: request.vehicle_make || "", vehicle_model: request.vehicle_model || "",
          notes: request.issue || "",
          items: [{ description: bid.message?.trim() || request.issue?.trim() || "Repair per accepted estimate", quantity: 1, unit_price: bid.amount ?? 0, kind: "labor" }],
        },
      },
    });
  };

  return (
    <Layout title="My Bids">
      {loading ? (
        <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
      ) : bids.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-16 text-center animate-fade-up">
          <Send className="h-10 w-10 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400 mb-4">You haven't sent any estimates yet.</p>
          <Button data-testid="mybids-goto-board" onClick={() => navigate("/jobs")} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">Browse the Job Board</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-up">
          {bids.map(({ bid, request, client }) => {
            const vehicle = [request.vehicle_year, request.vehicle_make, request.vehicle_model].filter(Boolean).join(" ");
            const s = STATUS[bid.status] || STATUS.pending;
            const Icon = s.icon;
            const completed = request.job_completed;
            return (
              <div key={bid.id} data-testid={`mybid-${bid.id}`} className="rounded-xl border border-slate-800 bg-slate-900 p-5">
                <div className="flex items-start justify-between">
                  <p className="font-semibold text-slate-100 flex items-center gap-2"><Car className="h-4 w-4 text-slate-500" /> {vehicle || "Vehicle"}</p>
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 border ${completed ? "text-blue-400 bg-blue-400/10 border-blue-400/20" : s.cls}`}>
                    <Icon className="h-3 w-3" /> {completed ? "Completed" : s.label}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-1 flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-600" /> {request.place_name || ""} {request.zip_code}</p>
                {request.issue && <p className="text-sm text-slate-400 mt-2">{request.issue}</p>}
                <div className="mt-3 pt-3 border-t border-slate-800 flex items-center justify-between text-sm">
                  <span className="text-slate-500">Your quote</span>
                  <span className="font-bold text-amber-400">{bid.amount != null ? money(bid.amount) : "—"}</span>
                </div>
                {bid.status === "accepted" && client && (
                  <div className="mt-3 pt-3 border-t border-slate-800 space-y-1.5" data-testid={`mybid-contact-${bid.id}`}>
                    <p className="text-[11px] uppercase tracking-wide text-emerald-400 font-semibold">Client contact unlocked</p>
                    <p className="text-slate-200 text-sm flex items-center gap-2"><User className="h-3.5 w-3.5 text-slate-500" /> {client.name}</p>
                    <p className="text-slate-200 text-sm flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-500" /> {client.phone}</p>
                    {client.email && <p className="text-slate-300 text-sm flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 text-slate-500" /> {client.email}</p>}
                    <Button size="sm" data-testid={`create-estimate-${bid.id}`} onClick={() => createEstimate({ bid, request, client })}
                      className="mt-2 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                      <FileText className="h-4 w-4 mr-1.5" /> {request.estimate_sent ? "Update Estimate" : "Create Estimate"}
                    </Button>
                    {request.estimate_sent && (
                      <div data-testid={`mybid-estimate-status-${bid.id}`} className="mt-2 text-xs flex items-center gap-1.5">
                        <span className="text-slate-500">Estimate {request.estimate_total != null ? money(request.estimate_total) : ""}:</span>
                        {request.estimate_status === "approved" ? (
                          <span className="text-emerald-400 font-semibold flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Approved by client</span>
                        ) : request.estimate_status === "declined" ? (
                          <span className="text-rose-400 font-semibold flex items-center gap-1"><XCircle className="h-3.5 w-3.5" /> Declined</span>
                        ) : (
                          <span className="text-amber-400 font-semibold flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Awaiting approval</span>
                        )}
                      </div>
                    )}
                    {!completed ? (
                      <Button size="sm" variant="outline" data-testid={`complete-job-${bid.id}`} onClick={() => completeJob(request.id)} disabled={completing === request.id}
                        className="mt-2 w-full bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800">
                        {completing === request.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Flag className="h-4 w-4 mr-1.5" />} Mark job completed
                      </Button>
                    ) : request.mechanic_rated_client ? (
                      <p data-testid={`client-rated-${bid.id}`} className="mt-2 text-xs text-emerald-400 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> You rated this client</p>
                    ) : (
                      <RateClientForm reqId={request.id} onDone={load} />
                    )}
                  </div>
                )}
                <p className="text-[11px] text-slate-600 mt-3">Sent {fmtDate(bid.created_at)}</p>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
